import {
  EAssetMutationType,
  EAssetSource,
  EAssetType,
  EBranchMutationType,
  ECaseState,
  EDynamicTemplateType,
  EEstateOrbitRenderingMode,
  EFieldMappingProperty,
  EFieldType,
  EFieldVisibility,
  EImageToVideoMode,
  EMediaSequenceState,
  EMediaSequenceType,
  EPosition,
  EPublicationState,
  EResizeMode,
  EServiceTicketState,
  ETemplatePosition,
  ETranscriptionState,
  ETranslationTermNamespace,
  EUpdateType,
} from 'api/core';

const UNKNOWN_TYPE = 'Ukendt type';

export const publicationStateTranslate = (state: EPublicationState) =>
  translate({
    value: state,
    map: {
      [EPublicationState.Draft]: 'Kladde',
      [EPublicationState.Scheduled]: 'Planlagt',
    },
    default: UNKNOWN_TYPE,
  });

export const caseStateTranslate = (state?: ECaseState) =>
  translate({
    value: state as ECaseState,
    map: {
      [ECaseState.Available]: 'Ledig',
      [ECaseState.Unavailable]: 'Ej ledig',
      [ECaseState.Reserved]: 'Reserveret',
      [ECaseState.Deactivated]: 'Deaktiveret',
    },
    default: UNKNOWN_TYPE,
  });

export const translationTermNamespaceTranslate = (
  namespace: ETranslationTermNamespace
) =>
  translate({
    value: namespace,
    map: {
      [ETranslationTermNamespace.CoreField]: 'Projektfelt',
      [ETranslationTermNamespace.EstateOrbitFrontend]: 'Boligvælger frontend',
      [ETranslationTermNamespace.CoreBackend]: 'Core backend',
      [ETranslationTermNamespace.CoreFrontend]: 'Core frontend',
      [ETranslationTermNamespace.Unknown]: UNKNOWN_TYPE,
    },
    default: UNKNOWN_TYPE,
  });

export const fieldTypeTranslate = (state: EFieldType) =>
  translate({
    value: state,
    map: {
      [EFieldType.Text]: 'Tekst',
      [EFieldType.Area]: 'Areal',
      [EFieldType.SubsidyArea]: 'Boligstøtte areal',
      [EFieldType.Boolean]: 'Boolsk udtryk (ja/nej)',
      [EFieldType.Currency]: 'Valuta',
      [EFieldType.Date]: 'Dato',
      [EFieldType.Number]: 'Tal',
      [EFieldType.Rent]: 'Husleje',
      [EFieldType.HousingType]: 'Boligtype',
      [EFieldType.Longtext]: 'Lang tekst',
      [EFieldType.ListingType]: 'Boligtype',
    },
    default: UNKNOWN_TYPE,
  });

export const mappingfieldPropertyTranslate = (
  EMappingProperty: EFieldMappingProperty
) =>
  translate({
    value: EMappingProperty,
    map: {
      [EFieldMappingProperty.Rent]: 'Husleje',
      [EFieldMappingProperty.Area]: 'Areal',
      [EFieldMappingProperty.Rooms]: 'Værelser',
      [EFieldMappingProperty.Deposit]: 'Depositum',
      [EFieldMappingProperty.UtilitiesWater]: 'Aconto vand',
      [EFieldMappingProperty.UtilitiesElectricity]: 'Aconto el',
      [EFieldMappingProperty.UtilitiesHeating]: 'Aconto varme',
      [EFieldMappingProperty.UtilitiesTotal]: 'Aconto i alt',
      [EFieldMappingProperty.BalconyArea]: 'Altanareal',
      [EFieldMappingProperty.IdleRent]: 'Tomgangsleje',
      [EFieldMappingProperty.AvailableFrom]: 'Overtagelsesdato',
    },
    default: UNKNOWN_TYPE,
  });

export const fieldVisibilityTranslate = (state: EFieldVisibility) =>
  translate({
    value: state,
    map: {
      [EFieldVisibility.AlwaysHidden]: 'Altid skjult',
      [EFieldVisibility.AlwaysVisible]: 'Altid synlig',
      [EFieldVisibility.DesktopOnly]: 'Kun synlig på desktop',
      [EFieldVisibility.MobileOnly]: 'Kun synlig på mobil',
    },
    default: UNKNOWN_TYPE,
  });

export const assetMutationTranslate = (type: EAssetMutationType) =>
  translate({
    value: type,
    map: {
      [EAssetMutationType.ColorAdjustments]: 'Farvejustering',
      [EAssetMutationType.AudioAdjustments]: 'Lydjustering',
      [EAssetMutationType.TrimVideo]: 'Klip video',
      [EAssetMutationType.ImageToVideo]: 'Billede til video',
      [EAssetMutationType.ImageOverlay]: 'Billede overlejring',
      [EAssetMutationType.Voiceover]: 'Speak',
      [EAssetMutationType.AddDynamicOverlay]: 'Dynamisk overlejring',
      [EAssetMutationType.Playback]: 'Afspilningsjustering',
      [EAssetMutationType.None]: 'Ingen',
    },
    default: UNKNOWN_TYPE,
  });

export const assetMutationTranslateProcessingMessage = (
  type: EAssetMutationType
) =>
  translate({
    value: type,
    map: {
      [EAssetMutationType.ColorAdjustments]: 'Anvender farvejustering',
      [EAssetMutationType.AudioAdjustments]: 'Anvender lydjustering',
      [EAssetMutationType.Playback]: 'Anvender afspilningsjustering',
      [EAssetMutationType.TrimVideo]: 'Klipper video',
      [EAssetMutationType.ImageToVideo]: 'Konverterer billede til video',
      [EAssetMutationType.ImageOverlay]: 'Anvender billede overlejring',
      [EAssetMutationType.Voiceover]: 'Tilføjer speak til billede',
      [EAssetMutationType.AddDynamicOverlay]: 'Anvender dynamisk overlejring',
      [EAssetMutationType.None]: 'Ingen',
    },
    default: UNKNOWN_TYPE,
  });

export const branchMutationTranslate = (type: EBranchMutationType) =>
  translate({
    value: type,
    map: {
      [EBranchMutationType.BakeSubtitles]: 'Indsæt undertekster',
      [EBranchMutationType.AddBackgroundMusic]: 'Baggrundsmusik',
      [EBranchMutationType.AddProgressBar]: 'Afspilningsbjælke',
      [EBranchMutationType.AddWatermark]: 'Vandmærke',
      [EBranchMutationType.AudioAdjustments]: 'Lydjustering',
      [EBranchMutationType.GenerateSubtitles]: 'Generer undertekster',
      [EBranchMutationType.ResizeAndConcat]:
        'Størrelsestilpasning og sammensætning',
      [EBranchMutationType.AddDynamicOverlay]: 'Dynamisk overlejring',
    },
    default: UNKNOWN_TYPE,
  });

export const branchMutationTranslateProcessingMessage = (
  type: EBranchMutationType
) =>
  translate({
    value: type,
    map: {
      [EBranchMutationType.BakeSubtitles]: 'Indsætter undertekster',
      [EBranchMutationType.AddBackgroundMusic]: 'Tilføjer baggrundsmusik',
      [EBranchMutationType.AddProgressBar]: 'Tilføjer afspilningsbjælke',
      [EBranchMutationType.AddWatermark]: 'Tilføjer vandmærke',
      [EBranchMutationType.AudioAdjustments]: 'Udfører lydjusteringer',
      [EBranchMutationType.GenerateSubtitles]: 'Transskriberer undertekster',
      [EBranchMutationType.ResizeAndConcat]:
        'Udfører størrelsestilpasning og sammensætter til én video',
      [EBranchMutationType.AddDynamicOverlay]: 'Anvender dynamisk overlejring',
    },
    default: UNKNOWN_TYPE,
  });

export const imageToVideoModeTranslate = (mode: EImageToVideoMode) =>
  translate({
    value: mode,
    map: {
      [EImageToVideoMode.PanLeftToRight]: 'Venstre mod højre',
      [EImageToVideoMode.PanTopLeftToBottomRight]:
        'Venstre mod nederst til højre',
      [EImageToVideoMode.Still]: 'Stillebillede',
      [EImageToVideoMode.ZoomIn]: 'Zoom ind',
      [EImageToVideoMode.KenBurns]: 'Ken Burns-effekten',
      [EImageToVideoMode.ZoomOut]: 'Zoom ud',
      [EImageToVideoMode.Automatic]: 'Automatisk',
    },
    default: UNKNOWN_TYPE,
  });

export const dynamicTemplateTypeTranslate = (type: EDynamicTemplateType) =>
  translate({
    value: type,
    map: {
      [EDynamicTemplateType.Overlay]: 'Overlejring',
      [EDynamicTemplateType.Transition]: 'Overgangseffekt',
    },
    default: UNKNOWN_TYPE,
  });

export const positionTranslate = (mode: EPosition | ETemplatePosition) =>
  translate({
    value: mode,
    map: {
      [ETemplatePosition.First]: 'Først',
      [ETemplatePosition.Last]: 'Sidst',
      [EPosition.TopLeft]: 'Øverst til venstre',
      [EPosition.TopCenter]: 'Øverst i midten',
      [EPosition.TopRight]: 'Øverst til højre',
      [EPosition.BottomLeft]: 'Nederst til venstre',
      [EPosition.BottomCenter]: 'Nederst i midten',
      [EPosition.BottomRight]: 'Nederst til højre',
      [EPosition.FullScreen]: 'Hele skærmen',
    },
    default: UNKNOWN_TYPE,
  });

export const resizeTranslate = (mode: EResizeMode) =>
  translate({
    value: mode,
    map: {
      [EResizeMode.Crop]: 'Crop / beskær',
      [EResizeMode.CropWithBlurredPadding]: 'Crop med evt. sløret letterbox',
      [EResizeMode.LetterboxPadding]: 'Letterbox',
      [EResizeMode.Forced]: 'Tvungen',
    },
    default: UNKNOWN_TYPE,
  });

export const mediaSequenceStateTranslate = (state: EMediaSequenceState) =>
  translate({
    value: state,
    map: {
      [EMediaSequenceState.Draft]: 'Kladde',
      [EMediaSequenceState.Processed]: 'Færdig',
      [EMediaSequenceState.Processing]: 'I gang',
      [EMediaSequenceState.Failed]: 'Fejlet',
      [EMediaSequenceState.Published]: 'Udgivet',
    },
    default: UNKNOWN_TYPE,
  });

export const mediaSequenceTypeTranslate = (type: EMediaSequenceType) =>
  translate({
    value: type,
    map: {
      [EMediaSequenceType.Normal]: 'Normal',
      [EMediaSequenceType.Strict]: 'Strict',
      [EMediaSequenceType.Template]: 'Template',
      [EMediaSequenceType.StrictTemplate]: 'StrictTemplate',
    },
    default: UNKNOWN_TYPE,
  });

export const transcriptionStateTranslate = (state?: ETranscriptionState) =>
  translate({
    value: state as ETranscriptionState,
    map: {
      [ETranscriptionState.NotApplicable]: 'Ikke relevant',
      [ETranscriptionState.Approved]: 'Godkendt',
      [ETranscriptionState.Generated]: 'Genereret',
      [ETranscriptionState.Pending]: 'Afventer',
    },
    default: UNKNOWN_TYPE,
  });

export const updateTypeTranslate = (state: EUpdateType) =>
  translate({
    value: state,
    map: {
      [EUpdateType.Failed]: 'Fejlet',
      [EUpdateType.Invoked]: 'Påbegyndt',
      [EUpdateType.Processed]: 'Færdiggjort',
      [EUpdateType.ProcessedWithMissingFrames]:
        'Færdiggjort (med manglende frames)',
      [EUpdateType.Processing]: 'Påbegyndt',
      [EUpdateType.Resumed]: 'Genoptaget',
    },
    default: UNKNOWN_TYPE,
  });

export const assetTypeTranslate = (type: EAssetType) =>
  translate({
    value: type,
    map: {
      [EAssetType.Image]: 'Billede',
      [EAssetType.Video]: 'Video',
      [EAssetType.Audio]: 'Lyd',
      [EAssetType.Document]: 'Dokument',
      [EAssetType.Other]: 'Andet',
    },
    default: UNKNOWN_TYPE,
  });

export const assetSourceTranslate = (type: EAssetSource) =>
  translate({
    value: type,
    map: {
      [EAssetSource.Unknown]: 'Ukendt',
      [EAssetSource.SystemSeeded]: 'Systemfil',
      [EAssetSource.UserUploaded]: 'Bruger uploadet',
      [EAssetSource.MediaOrbitGeneated]: 'Smart Video genereret',
      [EAssetSource.ServiceTicket]: 'Supportsag',
      [EAssetSource.External]: 'Ekstern kilde',
    },
    default: UNKNOWN_TYPE,
  });

export const estateOrbitRenderingModeTranslate = (
  mode: EEstateOrbitRenderingMode
) =>
  translate({
    value: mode,
    map: {
      [EEstateOrbitRenderingMode.WithIndicators]: 'Med indikatorer',
      [EEstateOrbitRenderingMode.FilledMasks]: 'Fyldte masker',
      [EEstateOrbitRenderingMode.WithoutMasks]: 'Skjul masker',
    },
    default: UNKNOWN_TYPE,
  });

export const serviceTicketStateTranslate = (type: EServiceTicketState) =>
  translate({
    value: type,
    map: {
      [EServiceTicketState.AwaitingCustomer]: 'Afventer kunde',
      [EServiceTicketState.AwaitingSupport]: 'Afventer support',
      [EServiceTicketState.AwaitingRelease]: 'Afventer frigivelse',
      [EServiceTicketState.Deleted]: 'Arkiveret',
      [EServiceTicketState.Done]: 'Færdig',
    },
    default: UNKNOWN_TYPE,
  });

function translate<T extends string | number>(params: {
  map: Record<T, string>;
  value: T;
  default: string;
}): string {
  return params.map[params.value] ?? params.default;
}
